import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { SignInLogo } from '~src/authentication/components/loginSpace/SignInLogo';
import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { BrowserTitleService } from '~src/commonInfra/services/BrowserTitleService';
import { useUsage } from '~src/spacesPlatform/hooks/useUsage';
import { cssClass } from '~src/style';

import { ALLOWED_PROTOCOLS, createExtraUsageParameters, EXTERNAL_LINK_SAFETY_FEATURE_NAME, MAX_URL_LENGTH } from './UseSafeExternalLinks';

export type LeavingProps = {
  externalHref: string;
  proceedToExternalURL: (externalURL: string) => void;
  stayOnTableau: () => void;
};

export function Leaving({ externalHref, proceedToExternalURL, stayOnTableau }: LeavingProps) {
  const usageParameters = createExtraUsageParameters(externalHref);

  const sendClickThroughEvent = useUsage(EXTERNAL_LINK_SAFETY_FEATURE_NAME, usageParameters);

  const handleChoiceToStay = () => {
    stayOnTableau?.();
  };

  const handleChoiceToLeave = () => {
    proceedToExternalURL?.(externalHref);
    sendClickThroughEvent(usageParameters);
  };

  BrowserTitleService.setTitleWithTranslatedSpace({ translatedSpaceName: Messages.externalLinkDialogWarningTitle()});

  return (
    <div className={containerClass}>
      <LogoForExternalLinkWarning />
      <LeavingContentBody externalHref={externalHref} />
      <div className={buttonContainerClass}>
        <ButtonWidget testId={STAY_HERE_BUTTON_TEST_ID}
                      buttonType={ButtonType.Outline}
                      onClick={handleChoiceToStay}>
                      {Messages.cancel()}
        </ButtonWidget>
        <ButtonWidget testId={PROCEED_TO_EXTERNAL_LINK_BUTTON_TEST_ID}
                      buttonType={ButtonType.Primary}
                      onClick={handleChoiceToLeave}>
                      {Messages.externalLinkDialogOpenLinkActionLabel()}
        </ButtonWidget>
      </div>
    </div>
  );
}

export function LeavingUrlBlocked() {
  BrowserTitleService.setTitleWithTranslatedSpace({ translatedSpaceName: Messages.externalLinkBlockedPageTitle()});
  return (
    <div className={containerClass}>
      <LogoForExternalLinkWarning />
      <p>{Messages.externalLinkBlockedMessage()}</p>
      <p>{Messages.externalLinkBlockedWorkaroundMessage()}</p>
    </div>
  );
}

export function LeavingContentBody({ externalHref }: { externalHref: string }) {
  return (
    <div>
      <p className={warningTextStyle} data-tb-test-id={EXTERNAL_LINK_WARNING_MESSAGE_TEST_ID}>
        {Messages.externalLinkDialogWarningMessage({
          EXTERNAL_URL: L10n.elideIfLong(externalHref, 50)
        })}
      </p>
    </div>
  );
}

export function LeavingInterstitial({ search }: { search: string }) {
  const safeRedirectTarget = ensureSafeRedirectTarget(search);

  return (
    <div className={interstitialPageStyle}>
      { safeRedirectTarget
        ? <Leaving externalHref={safeRedirectTarget} proceedToExternalURL={url => window.location.replace(url)} stayOnTableau={() => window.close()} />
        : <LeavingUrlBlocked />
      }
    </div>
  );
}

export const EXTERNAL_LINK_WARNING_MESSAGE_TEST_ID = 'external-location-text';
export const PROCEED_TO_EXTERNAL_LINK_BUTTON_TEST_ID = 'proceed-to-external-link';
export const STAY_HERE_BUTTON_TEST_ID = 'stay-here-link';

function LogoForExternalLinkWarning() {
  return <SignInLogo />;
}

function ensureSafeRedirectTarget(search: string): string | false {
  const params = new URLSearchParams(search);
  const raw = params.get('to');
  if (!raw) return false;
  if (raw.length > MAX_URL_LENGTH) return false;

  // Reject obvious control chars / attempts to break parsing or UI
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(raw)) return false;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }

  // Reject urls with credentials to avoid userinfo tricks: https://user:pass@evil.tld
  if (url.username || url.password) return false;

  // Reject javascript:, data:, blob:, file:, about:, etc.
  const scheme = url.protocol.toLowerCase();
  if (!ALLOWED_PROTOCOLS.has(scheme)) return false;

  return url.toString();
}

const externalUrlStyle = cssClass('leaving', 'external-location', {
  fontWeight: 'bold',
  fontStyle: 'italics',
  textAlign: 'left',
});

const containerClass = cssClass('leaving', 'container', {
  width: TabDialogWidth.width375px,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignContent: 'center',
});

const warningTextStyle = cssClass('leaving', 'warning-text', {
  textAlign: 'left'
});

const interstitialPageStyle = cssClass('leaving', 'interstitial', {
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  $nest: {
    [`&>.${containerClass}`]: {
      height: '100vh'
    }
  }
});

const buttonContainerClass = cssClass('leaving', 'buttons', {
  display: 'flex',
  height: '48px',
  flexDirection: 'row',
  justifyContent: 'right',
  gap: '16px'
});